import { trunc2 } from '@/utils';

/**
 * Centralized pricing constants + forward-calc helpers for the
 * quote/invoice/checkout flows.
 *
 * Before this file, FET_RATE / SEGMENT_FEE_PER_PAX / CREDIT_CARD_FEE_RATE
 * and the forward-calc shape were duplicated across AircraftCard,
 * EditPricingDrawer, CardInfoForm, GenerateQuote, and 6+ email
 * templates. Centralizing here means a rate change (e.g. FET
 * 7.5% → 8%) is a one-line edit, and the TR-2610-01318 class
 * (bid_option.final_price persisted without one of the taxes)
 * can be detected consistently anywhere.
 *
 * Any new pricing screen MUST read from these constants and use
 * `computeForwardPricing` for recomputations. Don't inline new
 * magic numbers or formulas elsewhere.
 */

// Tax + fee rates
export const FET_RATE = 0.075;                      // Federal Excise Tax — 7.5% of taxable base
export const BROKER_MARGIN_RATE = 0.15;             // Default broker margin — 15% of operator cost
export const SEGMENT_FEE_PER_PAX = 5.20;            // IRS segment tax — $5.20 per passenger per leg
export const INTL_FACILITIES_TAX_PER_PAX = 22.90;   // International facilities tax — $22.90 per passenger (applied only on international legs)
export const CREDIT_CARD_FEE_RATE = 0.03;           // Merchant surcharge — flat 3% of billable total

/**
 * Multiplier applied to per-leg tax rates (segment, intl) based on
 * the trip's leg count. One-way = 1, round-trip = 2, multi-leg =
 * the number of legs on the itinerary. Returns flightsLength
 * verbatim on multi-leg to match the pre-centralization behavior
 * in AircraftCard + EditPricingDrawer (same formula, same edge-case
 * handling).
 */
export const getLegMultiplier = (flightType, flightsLength) => {
  if (flightType === 'round-trip') return 2;
  if (flightType === 'multi-leg') return flightsLength;
  return 1;
};

/**
 * Build a per-option resolver that returns the HISTORICAL platform
 * fee rate the quote was priced at — back-solved from the stored
 * platform_fee and (subtotal + totalTaxes). Use this instead of
 * `resolveForSubtotal` from usePlatformFeeRate when editing a quote
 * that has already left DRAFT, so a mid-cycle admin rate change
 * doesn't silently re-price what the client has already seen.
 *
 * Returns `{ rate, percent }` matching usePlatformFeeRate's shape.
 * Returns `{ rate: 0, percent: 0 }` when back-solve isn't possible
 * (missing stored platform_fee, zero base) — preserves the stored
 * amount verbatim on save.
 */
export const makeHistoricalRateResolver = (opt) => () => {
  const taxable = (parseFloat(opt?.operator_cost) || 0)
    + (parseFloat(opt?.broker_markup) || 0);
  const taxes = (parseFloat(opt?.federal_excise_tax) || 0)
    + (parseFloat(opt?.segment_tax) || 0)
    + (parseFloat(opt?.international_tax) || 0);
  const stored = parseFloat(opt?.platform_fee) || 0;
  const base = taxable + taxes;
  if (!(base > 0) || !(stored > 0)) return { rate: 0, percent: 0 };
  const rate = stored / base;
  return { rate, percent: rate * 100 };
};

/**
 * Forward calc for an existing bid_option. Reuses the same precedence
 * the EditPricingDrawer's init effect uses (saved value wins, falls
 * through to formula when truly missing / NaN).
 *
 * @param {Object} opt - Bid option with operator_cost, broker_markup,
 *   federal_excise_tax, segment_tax, international_tax, platform_fee,
 *   is_domestic, is_international.
 * @param {Object} ctx - { passengerCount, legMultiplier, resolvePlatformFeeRate? }.
 *   When `resolvePlatformFeeRate` is supplied, platform fee is
 *   recomputed from the formula `(subtotal + totalTaxes) × rate`
 *   using the resolver's bracket-aware rate — matches the drawer's
 *   forward calc at EditPricingDrawer.jsx:681 and catches rows
 *   where the persisted platform_fee itself was computed wrong
 *   (e.g. only `subtotal × rate` without the taxes layer).
 *   When omitted, falls back to the persisted platform_fee.
 * @returns {{ fetEff, segEff, intlEff, platformFeeEff, totalTaxesEff, expectedFinal }}
 */
export const computeForwardPricing = (opt, { passengerCount, legMultiplier, resolvePlatformFeeRate }) => {
  const opCost = parseFloat(opt?.operator_cost) || 0;
  const brokerMkup = parseFloat(opt?.broker_markup) || 0;
  const taxable = opCost + brokerMkup;

  // isDomestic defaults to true when the saved flag is missing
  // (legacy quotes). isIntl keys off aircraft.is_international alone.
  const isDomesticEff = opt?.is_domestic === undefined || opt?.is_domestic === null
    ? true
    : !!opt.is_domestic;
  const isIntlEff = !!opt?.is_international;

  // FET: saved value if present (including 0 — intentional), else
  // auto-compute from taxable × rate. Gated by isDomestic.
  const savedFet = parseFloat(opt?.federal_excise_tax);
  const fetValue = !isNaN(savedFet) ? savedFet : trunc2(taxable * FET_RATE);
  const fetEff = isDomesticEff ? fetValue : 0;

  // Segment: saved amount if present (>= 0), else formula. Gated by isDomestic.
  const savedSegment = parseFloat(opt?.segment_tax);
  const segFromFormula = passengerCount * SEGMENT_FEE_PER_PAX * legMultiplier;
  const segValue = Number.isFinite(savedSegment) && savedSegment >= 0
    ? savedSegment
    : segFromFormula;
  const segEff = isDomesticEff ? segValue : 0;

  // International: saved amount if present (>= 0), else formula. Gated by isIntl.
  const savedIntl = parseFloat(opt?.international_tax);
  const intlFromFormula = passengerCount * INTL_FACILITIES_TAX_PER_PAX;
  const intlValue = Number.isFinite(savedIntl) && savedIntl >= 0
    ? savedIntl
    : intlFromFormula;
  const intlEff = isIntlEff ? intlValue : 0;

  const totalTaxesEff = fetEff + segEff + intlEff;

  // Platform fee: recompute if a resolver was supplied (matches the
  // drawer's forward calc, catches persisted-but-wrong platform_fee).
  // Otherwise fall back to the stored number.
  let platformFeeEff;
  if (typeof resolvePlatformFeeRate === 'function') {
    const resolved = resolvePlatformFeeRate(taxable) || {};
    const rate = Number.isFinite(resolved.rate) ? resolved.rate : 0;
    platformFeeEff = trunc2((taxable + totalTaxesEff) * rate);
  } else {
    const persistedPF = parseFloat(opt?.platform_fee);
    platformFeeEff = Number.isFinite(persistedPF) ? persistedPF : 0;
  }

  const expectedFinal = trunc2(taxable + platformFeeEff + totalTaxesEff);

  return { fetEff, segEff, intlEff, platformFeeEff, totalTaxesEff, expectedFinal };
};

/**
 * Compare a bid_option's persisted final_price against the forward-
 * calc expected value. Returns true when they diverge by more than
 * $0.02 (the trunc2 tolerance across the chain) — i.e. the row is
 * in the TR-2610-01318 state (one of the taxes isn't in final_price).
 */
export const isPricingInconsistent = (opt, ctx) => {
  const persistedFinal = parseFloat(opt?.final_price);
  if (!Number.isFinite(persistedFinal) || persistedFinal <= 0) return false;
  const { expectedFinal } = computeForwardPricing(opt, ctx);
  return Math.abs(expectedFinal - persistedFinal) > 0.02;
};
